import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Selection, Paging, Scrolling } from 'devextreme-react/data-grid';
import Form, { GroupItem, SimpleItem, Label } from "devextreme-react/form";
import { FormListBaseToolbar } from '../base';
import { Button, CheckBox } from 'devextreme-react';
import { Item } from 'devextreme-react/toolbar';
import { Popup } from 'devextreme-react/popup';
import { custom } from 'devextreme/ui/dialog';
import useApi from '../../hooks/useApi';
import ICodeBarriereGestionItem from '../../models/ICodeBarriereGestionItem';
import Guid from '../../models/Guid';
import { DataGridRef } from 'devextreme-react/data-grid';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../contexts/auth';
import ImpressionCouponBarriere from './impression-coupon-barriere';

interface IAjouterCodeManuelForm {
  nom: string,
  prenom: string,
  code: number | null,
  dateDebut: Date,
  dateFin: Date,
  famille: number,
  carte: number,
}

interface IModifierDatesForm {
  modifierDateDebut: boolean,
  dateDebut: Date,
  modifierDateFin: boolean,
  dateFin: Date,
  utiliserDatesSource: boolean,
  typeSourceDates: string,
}

const defaultAjouterCodeForm = (): IAjouterCodeManuelForm => ({
  nom: '',
  prenom: '',
  code: null,
  dateDebut: new Date(),
  dateFin: new Date(),
  famille: 0,
  carte: 0,
});

const defaultModifierDatesForm = (reference?: ICodeBarriereGestionItem): IModifierDatesForm => ({
  modifierDateDebut: false,
  dateDebut: reference?.dateDebut ? new Date(reference.dateDebut) : new Date(),
  modifierDateFin: true,
  dateFin: reference?.dateFin ? new Date(reference.dateFin) : new Date(),
  utiliserDatesSource: false,
  typeSourceDates: 'reservation',
});

const sourceDatesOptions = [
  { value: 'reservation', text: 'réservation courante' },
];

export default function GestionCodesBarriere() {
  const { user } = useAuth();
  const { getData: getCodeBarrieres, postData, isLoading } = useApi<ICodeBarriereGestionItem[]>();
  const [codes, setCodes] = useState<ICodeBarriereGestionItem[]>([]);
  const [afficherExpiresSeulement, setAfficherExpiresSeulement] = useState(false);
  const [dateFin, setDateFin] = useState<Date>(new Date());
  const [isPopupAjouterVisible, setIsPopupAjouterVisible] = useState(false);
  const [isPopupModifierDatesVisible, setIsPopupModifierDatesVisible] = useState(false);
  const [ajouterCodeForm, setAjouterCodeForm] = useState<IAjouterCodeManuelForm>(defaultAjouterCodeForm);
  const [modifierDatesForm, setModifierDatesForm] = useState<IModifierDatesForm>(defaultModifierDatesForm());
  const [idsModifierDates, setIdsModifierDates] = useState<Guid[]>([]);
  const [codesImpression, setCodesImpression] = useState<ICodeBarriereGestionItem[]>([]);
  const dataGridRef = useRef<DataGridRef>(null);

  const loadCodes = useCallback(async () => {
    const dateFinParam = dateFin ? dateFin.toISOString().split('T')[0] : '';
    const data = await getCodeBarrieres({
      customPath: `codeBarriere/getCodeBarrieres?afficherExpiresSeulement=${afficherExpiresSeulement}&dateFin=${dateFinParam}`,
      returnOnly: true,
    });
    setCodes(data ?? []);
  }, [afficherExpiresSeulement, dateFin, getCodeBarrieres]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const getSelectedRows = useCallback(() => {
    return (dataGridRef.current?.instance().getSelectedRowsData() ?? []) as ICodeBarriereGestionItem[];
  }, []);

  const dateFinEditorOptions = useMemo(() => ({
    displayFormat: "yyyy-MM-dd",
    type: "date" as const,
    value: dateFin,
    onValueChanged: (e: { value: Date }) => {
      if (e.value) setDateFin(e.value);
    },
  }), [dateFin]);

  const afficherExpiresOptions = useMemo(() => ({
    value: afficherExpiresSeulement,
    text: "Afficher seulement les codes de barrière expirés",
    onValueChanged: (e: { value?: boolean }) => setAfficherExpiresSeulement(!!e.value),
  }), [afficherExpiresSeulement]);

  const selectionnerTout = useCallback(() => {
    dataGridRef.current?.instance().selectAll();
  }, []);

  const deselectionnerTout = useCallback(() => {
    dataGridRef.current?.instance().clearSelection();
  }, []);

  const ouvrirAjouterCodeManuel = useCallback(() => {
    setAjouterCodeForm(defaultAjouterCodeForm());
    setIsPopupAjouterVisible(true);
  }, []);

  const confirmerAjouterCodeManuel = useCallback(async () => {
    if (!ajouterCodeForm.nom?.trim() || !ajouterCodeForm.prenom?.trim()) {
      notify("Le nom et le prénom sont requis.", "warning", 3000);
      return;
    }
    if (!ajouterCodeForm.code || ajouterCodeForm.code < 10000 || ajouterCodeForm.code > 99999) {
      notify("Le code doit être un nombre à 5 chiffres.", "warning", 3000);
      return;
    }

    try {
      await postData({
        nom: ajouterCodeForm.nom.trim(),
        prenom: ajouterCodeForm.prenom.trim(),
        code: ajouterCodeForm.code,
        dateDebut: ajouterCodeForm.dateDebut,
        dateFin: ajouterCodeForm.dateFin,
        famille: ajouterCodeForm.famille ?? 0,
        carte: ajouterCodeForm.carte ?? 0,
        idUsager: user?.idUsager,
      }, 'codeBarriere/ajouterCodeManuel', true);

      setIsPopupAjouterVisible(false);
      notify("Le code a été ajouté.", "success", 3000);
      await loadCodes();
    } catch {
      notify("Impossible d'ajouter le code. Vérifiez la configuration Atrium ou si le code existe déjà.", "error", 4000);
    }
  }, [ajouterCodeForm, loadCodes, postData, user?.idUsager]);

  const effacerCodesSelectionnes = useCallback(async () => {
    const selectedRows = getSelectedRows();
    if (!selectedRows.length) {
      notify("Sélectionnez au moins un code de barrière.", "warning", 3000);
      return;
    }

    const dialog = custom({
      dragEnabled: true,
      title: "Effacer",
      messageHtml: `Voulez-vous effacer ${selectedRows.length} code(s) de barrière sélectionné(s) ?`,
      buttons: [
        { text: "Oui", onClick: (e) => ({ buttonText: e.component.option("text") }) },
        { text: "Non", onClick: (e) => ({ buttonText: e.component.option("text") }) },
      ],
    });

    const result = await dialog.show();
    if (result.buttonText !== "Oui") return;

    try {
      await postData({
        idCodesBarriere: selectedRows.map((row) => row.idCodeBarriere),
        idUsager: user?.idUsager,
      }, 'codeBarriere/supprimer', true);

      notify("Les codes sélectionnés ont été effacés.", "success", 3000);
      dataGridRef.current?.instance().clearSelection();
      await loadCodes();
    } catch {
      notify("Impossible d'effacer les codes sélectionnés.", "error", 4000);
    }
  }, [getSelectedRows, loadCodes, postData, user?.idUsager]);

  const imprimerCoupon = useCallback(() => {
    const selectedRows = getSelectedRows();
    if (!selectedRows.length) {
      notify("Sélectionnez au moins un code de barrière.", "warning", 3000);
      return;
    }
    setCodesImpression(selectedRows);
  }, [getSelectedRows]);

  const ouvrirModifierDates = useCallback(() => {
    const selectedRows = getSelectedRows();
    if (!selectedRows.length) {
      notify("Sélectionnez au moins un code de barrière.", "warning", 3000);
      return;
    }

    setIdsModifierDates(selectedRows.map((row) => row.idCodeBarriere));
    setModifierDatesForm(defaultModifierDatesForm(selectedRows[0]));
    setIsPopupModifierDatesVisible(true);
  }, [getSelectedRows]);

  const confirmerModifierDates = useCallback(async () => {
    const {
      modifierDateDebut,
      dateDebut,
      modifierDateFin,
      dateFin,
      utiliserDatesSource,
      typeSourceDates,
    } = modifierDatesForm;

    if (!utiliserDatesSource && !modifierDateDebut && !modifierDateFin) {
      notify("Cochez au moins une option à modifier.", "warning", 3000);
      return;
    }

    try {
      const result = await postData({
        idCodesBarriere: idsModifierDates,
        modifierDateDebut: utiliserDatesSource ? false : modifierDateDebut,
        dateDebut: utiliserDatesSource || !modifierDateDebut ? null : dateDebut,
        modifierDateFin: utiliserDatesSource ? false : modifierDateFin,
        dateFin: utiliserDatesSource || !modifierDateFin ? null : dateFin,
        utiliserDatesSource,
        typeSourceDates,
        idUsager: user?.idUsager,
      }, 'codeBarriere/modifierDates', true) as { nombreModifies?: number, nombreIgnores?: number };

      setIsPopupModifierDatesVisible(false);

      const modifies = result?.nombreModifies ?? 0;
      const ignores = result?.nombreIgnores ?? 0;

      if (modifies > 0 && ignores > 0) {
        notify(`${modifies} code(s) modifié(s), ${ignores} ignoré(s).`, "warning", 4000);
      } else if (modifies > 0) {
        notify(`${modifies} code(s) modifié(s).`, "success", 3000);
      } else {
        notify("Aucun code n'a pu être modifié.", "error", 4000);
      }

      dataGridRef.current?.instance().clearSelection();
      await loadCodes();
    } catch {
      notify("Impossible de modifier les dates.", "error", 4000);
    }
  }, [idsModifierDates, loadCodes, modifierDatesForm, postData, user?.idUsager]);

  const ajouterCodeManuelOptions = useMemo(() => ({
    icon: 'add',
    text: "Ajouter un code manuel",
    onClick: ouvrirAjouterCodeManuel,
  }), [ouvrirAjouterCodeManuel]);

  const effacerCodesOptions = useMemo(() => ({
    icon: 'trash',
    text: "Effacer le(s) code(s)",
    onClick: effacerCodesSelectionnes,
  }), [effacerCodesSelectionnes]);

  const imprimerCouponOptions = useMemo(() => ({
    icon: 'print',
    text: "Imprimer coupon",
    onClick: imprimerCoupon,
  }), [imprimerCoupon]);

  const modifierDatesOptions = useMemo(() => ({
    icon: 'event',
    text: "Modifier les dates",
    onClick: ouvrirModifierDates,
  }), [ouvrirModifierDates]);

  const codeEditorOptions = useMemo(() => ({
    min: 10000,
    max: 99999,
    showSpinButtons: true,
  }), []);

  const dateEditorOptions = useMemo(() => ({
    displayFormat: "yyyy-MM-dd",
    type: "date" as const,
  }), []);

  const dateTimeEditorOptions = useMemo(() => ({
    type: "datetime" as const,
    displayFormat: "dd-MM-yyyy HH:mm:ss",
    useMaskBehavior: true,
  }), []);

  const modifierDateDebutEditorOptions = useMemo(() => ({
    ...dateTimeEditorOptions,
    value: modifierDatesForm.dateDebut,
    disabled: !modifierDatesForm.modifierDateDebut || modifierDatesForm.utiliserDatesSource,
    onValueChanged: (e: { value: Date }) => {
      if (e.value) setModifierDatesForm((prev) => ({ ...prev, dateDebut: e.value }));
    },
  }), [dateTimeEditorOptions, modifierDatesForm.dateDebut, modifierDatesForm.modifierDateDebut, modifierDatesForm.utiliserDatesSource]);

  const modifierDateFinEditorOptions = useMemo(() => ({
    ...dateTimeEditorOptions,
    value: modifierDatesForm.dateFin,
    disabled: !modifierDatesForm.modifierDateFin || modifierDatesForm.utiliserDatesSource,
    onValueChanged: (e: { value: Date }) => {
      if (e.value) setModifierDatesForm((prev) => ({ ...prev, dateFin: e.value }));
    },
  }), [dateTimeEditorOptions, modifierDatesForm.dateFin, modifierDatesForm.modifierDateFin, modifierDatesForm.utiliserDatesSource]);

  const typeSourceDatesEditorOptions = useMemo(() => ({
    items: sourceDatesOptions,
    valueExpr: "value",
    displayExpr: "text",
    value: modifierDatesForm.typeSourceDates,
    disabled: !modifierDatesForm.utiliserDatesSource,
    onValueChanged: (e: { value: string }) => {
      if (e.value) setModifierDatesForm((prev) => ({ ...prev, typeSourceDates: e.value }));
    },
  }), [modifierDatesForm.typeSourceDates, modifierDatesForm.utiliserDatesSource]);

  return (
    <div className="base-form-content">
      <div className="form-left-content" style={{ width: '100%' }}>
        <FormListBaseToolbar
          headerText="Gestion des codes de barrière"
          ajouterOptions={{ visible: false }}
          modifierOptions={{ visible: false }}
          sauvegarderOptions={{ visible: false }}
          annulerOptions={{ visible: false }}
          effacerOptions={{ visible: false }}
        >
          <Item key={"toolbar-item-ajouter-manuel"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={ajouterCodeManuelOptions} />
          <Item key={"toolbar-item-effacer"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={effacerCodesOptions} />
          <Item key={"toolbar-item-modifier-dates"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={modifierDatesOptions} />
          <Item key={"toolbar-item-imprimer-coupon"} location={"before"} widget={"dxButton"} locateInMenu={"auto"} options={imprimerCouponOptions} />
        </FormListBaseToolbar>
        <div style={{ paddingTop: "1rem", flex: 1, overflow: "auto" }}>
          <Form alignItemLabelsInAllGroups={true} className={"forms"} labelLocation={"left"} showColonAfterLabel={true}>
            <GroupItem colCount={4}>
              <SimpleItem key={"frm-expires"} render={() => <CheckBox {...afficherExpiresOptions} />} />
              <SimpleItem key={"frm-dateFin"} dataField={"dateFin"} editorType={"dxDateBox"} editorOptions={dateFinEditorOptions}>
                <Label text={"Date fin"} />
              </SimpleItem>
              <SimpleItem key={"frm-select-all"} render={() => <Button text="Sélectionner tout" onClick={selectionnerTout} />} />
              <SimpleItem key={"frm-unselect-all"} render={() => <Button text="Déselectionner tout" onClick={deselectionnerTout} />} />
            </GroupItem>
            <GroupItem>
              <DataGrid
                ref={dataGridRef}
                dataSource={codes}
                keyExpr="idCodeBarriere"
                showBorders={true}
                allowColumnResizing={true}
                noDataText={isLoading ? "Chargement..." : "Aucun code de barrière"}
                height={"calc(100vh - 280px)"}
              >
                <Selection mode="multiple" showCheckBoxesMode="always" />
                <Scrolling mode="virtual" />
                <Paging enabled={false} />
                <Column dataField="nom" dataType="string" caption="Nom" />
                <Column dataField="prenom" dataType="string" caption="Prénom" />
                <Column dataField="dateDebut" dataType="date" caption="Date début" format="yyyy-MM-dd" />
                <Column dataField="dateFin" dataType="date" caption="Date fin" format="yyyy-MM-dd" />
                <Column dataField="acces" dataType="number" caption="Accès" />
                <Column dataField="carte" dataType="number" caption="Carte" />
                <Column dataField="famille" dataType="number" caption="Famille" />
                <Column dataField="code" dataType="number" caption="Code" />
              </DataGrid>
            </GroupItem>
          </Form>
        </div>
      </div>

      <Popup
        visible={isPopupModifierDatesVisible}
        onHiding={() => setIsPopupModifierDatesVisible(false)}
        dragEnabled={true}
        showTitle={true}
        title="Date d'accès à la barrière"
        width={520}
        height={"auto"}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CheckBox
              value={modifierDatesForm.modifierDateDebut}
              disabled={modifierDatesForm.utiliserDatesSource}
              onValueChanged={(e) => setModifierDatesForm((prev) => ({
                ...prev,
                modifierDateDebut: !!e.value,
                utiliserDatesSource: e.value ? false : prev.utiliserDatesSource,
              }))}
            />
            <span style={{ minWidth: '6rem' }}>Date début</span>
            <div style={{ flex: 1 }}>
              <Form>
                <SimpleItem editorType="dxDateBox" editorOptions={modifierDateDebutEditorOptions} />
              </Form>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CheckBox
              value={modifierDatesForm.modifierDateFin}
              disabled={modifierDatesForm.utiliserDatesSource}
              onValueChanged={(e) => setModifierDatesForm((prev) => ({
                ...prev,
                modifierDateFin: !!e.value,
                utiliserDatesSource: e.value ? false : prev.utiliserDatesSource,
              }))}
            />
            <span style={{ minWidth: '6rem' }}>Date de fin</span>
            <div style={{ flex: 1 }}>
              <Form>
                <SimpleItem editorType="dxDateBox" editorOptions={modifierDateFinEditorOptions} />
              </Form>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CheckBox
              value={modifierDatesForm.utiliserDatesSource}
              disabled={modifierDatesForm.modifierDateDebut || modifierDatesForm.modifierDateFin}
              onValueChanged={(e) => setModifierDatesForm((prev) => ({
                ...prev,
                utiliserDatesSource: !!e.value,
                modifierDateDebut: e.value ? false : prev.modifierDateDebut,
                modifierDateFin: e.value ? false : prev.modifierDateFin,
              }))}
            />
            <span style={{ minWidth: '6rem' }}>Utiliser les dates de la</span>
            <div style={{ flex: 1 }}>
              <Form>
                <SimpleItem editorType="dxSelectBox" editorOptions={typeSourceDatesEditorOptions} />
              </Form>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          <Button text="Ok" type="default" width={120} onClick={confirmerModifierDates} />
          <Button text="Annuler" width={120} onClick={() => setIsPopupModifierDatesVisible(false)} />
        </div>
      </Popup>

      <Popup
        visible={isPopupAjouterVisible}
        onHiding={() => setIsPopupAjouterVisible(false)}
        dragEnabled={true}
        showTitle={true}
        title="Ajouter un code manuel"
        width={500}
        height={"auto"}
      >
        <Form
          formData={ajouterCodeForm}
          onFieldDataChanged={(e) => {
            if (!e.dataField) return;
            setAjouterCodeForm((prev) => ({ ...prev, [e.dataField as string]: e.value }));
          }}
        >
          <GroupItem colCount={1}>
            <SimpleItem dataField="nom" editorType="dxTextBox" isRequired={true}>
              <Label text="Nom" />
            </SimpleItem>
            <SimpleItem dataField="prenom" editorType="dxTextBox" isRequired={true}>
              <Label text="Prénom" />
            </SimpleItem>
            <SimpleItem dataField="code" editorType="dxNumberBox" editorOptions={codeEditorOptions} isRequired={true}>
              <Label text="Code" />
            </SimpleItem>
            <SimpleItem dataField="dateDebut" editorType="dxDateBox" editorOptions={dateEditorOptions} isRequired={true}>
              <Label text="Date début" />
            </SimpleItem>
            <SimpleItem dataField="dateFin" editorType="dxDateBox" editorOptions={dateEditorOptions} isRequired={true}>
              <Label text="Date fin" />
            </SimpleItem>
            <SimpleItem dataField="famille" editorType="dxNumberBox">
              <Label text="Famille" />
            </SimpleItem>
            <SimpleItem dataField="carte" editorType="dxNumberBox">
              <Label text="Carte" />
            </SimpleItem>
          </GroupItem>
        </Form>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <Button text="Annuler" onClick={() => setIsPopupAjouterVisible(false)} />
          <Button text="Ajouter" type="default" onClick={confirmerAjouterCodeManuel} />
        </div>
      </Popup>

      {codesImpression.length > 0 &&
        <ImpressionCouponBarriere
          codes={codesImpression}
          onClose={() => setCodesImpression([])}
        />
      }
    </div>
  );
}
